import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type IconSize = "sm" | "md" | "lg";

const iconSizeClasses: Record<IconSize, string> = {
  sm: "h-8 w-8",
  md: "h-10 w-10",
  lg: "h-12 w-12"
};

interface EmptyStateBaseProps {
  /** Title text */
  title: string;
  /** Description text or element (supports ReactNode for links) */
  description?: React.ReactNode;
  /** Action button or other content below the description */
  children?: React.ReactNode;
  /** Additional className for the container */
  className?: string;
  /** Additional className for the description paragraph (e.g. to override max-width) */
  descriptionClassName?: string;
  /** Whether to use compact styling */
  compact?: boolean;
  /** `error` tints the icon destructive and defaults it to AlertTriangle */
  tone?: "default" | "error";
  /** Renders a "Try again" button under the description */
  onRetry?: () => void;
  /** Shows the retry button as loading */
  retrying?: boolean;
  /** Label for the retry button (default "Try again") */
  retryLabel?: string;
  /** data-testid for the retry button */
  retryTestId?: string;
}

interface EmptyStateWithIcon extends EmptyStateBaseProps {
  /** Simple icon component (lucide-react, react-icons, etc.); optional with tone="error" */
  icon?: React.ComponentType<{ className?: string }>;
  /** Size of the icon */
  iconSize?: IconSize;
  /** Full illustration component (SVG with built-in decoration). Mutually exclusive with icon. */
  illustration?: never;
}

interface EmptyStateWithIllustration extends EmptyStateBaseProps {
  /** Full illustration component (SVG with built-in decoration) */
  illustration: React.ReactElement;
  /** Simple icon component. Mutually exclusive with illustration. */
  icon?: never;
  /** Not used with illustration */
  iconSize?: never;
}

export type EmptyStateProps = EmptyStateWithIcon | EmptyStateWithIllustration;

export function EmptyState({
  icon,
  iconSize = "lg",
  illustration,
  title,
  description,
  children,
  className,
  descriptionClassName,
  compact = false,
  tone = "default",
  onRetry,
  retrying = false,
  retryLabel = "Try again",
  retryTestId,
  ...rest
}: EmptyStateProps & Omit<React.HTMLAttributes<HTMLDivElement>, keyof EmptyStateBaseProps | "icon" | "iconSize" | "illustration">) {
  const Icon = icon ?? (tone === "error" ? AlertTriangle : undefined);
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center text-center",
        compact ? "px-4 py-6" : "px-8 py-16",
        className
      )}
      {...rest}
    >
      {illustration ? (
        <div className={compact ? "mb-2" : "mb-4"}>
          {illustration}
        </div>
      ) : Icon ? (
        <div className={compact ? "mb-2" : "mb-4"}>
          <Icon
            className={cn(
              "mx-auto",
              tone === "error" ? "text-destructive" : "text-muted-foreground",
              iconSizeClasses[iconSize],
            )}
          />
        </div>
      ) : null}
      <h3
        className={cn(
          "font-medium text-foreground",
          compact ? "text-sm mb-1" : "text-base mb-1.5"
        )}
      >
        {title}
      </h3>
      {description && (
        <p
          className={cn(
            "text-muted-foreground leading-relaxed",
            compact ? "text-xs max-w-xs" : "text-sm max-w-md",
            descriptionClassName
          )}
        >
          {description}
        </p>
      )}
      {onRetry && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={compact ? "mt-3" : "mt-4"}
          onClick={onRetry}
          isLoading={retrying}
          data-testid={retryTestId}
        >
          {retryLabel}
        </Button>
      )}
      {children && (
        <div className={compact ? "mt-3" : "mt-4"}>
          {children}
        </div>
      )}
    </div>
  );
}
